import React from 'react'
import { BottomSheet, Button, Typography } from '@s4l/ds'
import { IconInfo } from '@s4l/icons/IconInfo'
import { usePriceDetailsHelpers } from './hooks'
import PriceDetailsContent from './PriceDetailsContent'
import { type RoomData, type TotalData, type WalletSummary } from './types'
import { withErrorBoundary } from '@s4l/utils/hocs/withErrorBoundary'

export interface PriceDetailsProps {
  nights: number
  rooms: RoomData[]
  variant?: 'bottomsheet' | 'inline'
  totalData?: TotalData
  wallet?: WalletSummary
  onWalletToggle?: (nextSelected: boolean) => void
}

function PriceDetails({
  nights,
  rooms,
  variant = 'bottomsheet',
  totalData,
  wallet,
  onWalletToggle,
}: Readonly<PriceDetailsProps>) {
  const { formatMessage } = usePriceDetailsHelpers()
  // Early validation for both variants

  if (!rooms || rooms.length === 0 || !nights || nights <= 0) {
    return null
  }

  // If variant is inline, render content directly with title
  if (variant === 'inline') {
    return (
      <>
        <div style={{ marginBottom: 'var(--space-8)' }}>
          <Typography variant="body" size="xl" bold color="var(--color-neutral-900)">
            {formatMessage({ id: 'global.price-breakdown' })}
          </Typography>
        </div>
        <PriceDetailsContent
          nights={nights}
          rooms={rooms}
          totalData={totalData}
          wallet={wallet}
          onWalletToggle={onWalletToggle}
        />
      </>
    )
  }

  // Default bottomsheet behavior
  return (
    <BottomSheet>
      <BottomSheet.Trigger asChild>
        <Button variant="icon-flat" size="extraSmall" leadingIcon={<IconInfo />} />
      </BottomSheet.Trigger>
      <BottomSheet.Content
        title={formatMessage({ id: 'global.price-breakdown' })}
        modalOnDesktop
        size="s"
      >
        <PriceDetailsContent
          nights={nights}
          rooms={rooms}
          totalData={totalData}
          wallet={wallet}
          onWalletToggle={onWalletToggle}
        />
      </BottomSheet.Content>
    </BottomSheet>
  )
}

PriceDetails.displayName = 'PriceDetails'
export default withErrorBoundary(PriceDetails)
